import { useNavigate } from 'react-router';
import { CurrencyChip, Icon, Spinner } from '../components/ui';
import { useIsMobile } from '../lib/hooks';
import { useHousehold } from '../lib/household';
import { useAccounts } from '../lib/queries';
import { useShell } from '../shell/Shell';
import '../styles/loans.css';
import { Navigate } from 'react-router';

/** Mobile Accounts tab: budget, tracking and debt accounts with native + base balances, and net worth. */
export function AccountsPage() {
  const { fmt } = useHousehold();
  const q = useAccounts();
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const { addAccount } = useShell();
  if (q.isPending) return <Spinner />;
  const accounts = (q.data ?? []).filter((a) => !a.closed);
  if (!mobile) return <Navigate to="/accounts/all" replace />;
  const section = (title: string, list: typeof accounts) =>
    list.length ? (
      <>
        <div className="h4" style={{ margin: '14px 18px 8px' }}>
          {title}
          <span>
            {fmt.num(list.reduce((s, a) => s + a.workingBase, 0))} {fmt.base}
          </span>
        </div>
        <div className="m-list">
          {list.map((a) => (
            <button key={a.id} className="m-li" onClick={() => navigate(`/accounts/${a.id}`)}>
              <CurrencyChip currency={a.currency} sm />
              <div className="t">
                {a.name}
                <small>
                  {a.institution || a.kind}
                  {a.unapprovedCount ? ` · ${a.unapprovedCount} unapproved` : ''}
                </small>
              </div>
              <div className="a">
                {fmt.money(a.working, a.currency, 2)}
                {a.currency !== fmt.base ? (
                  <span className="amt2">
                    ≈ {fmt.num(a.workingBase)} {fmt.base}
                  </span>
                ) : null}
              </div>
            </button>
          ))}
        </div>
      </>
    ) : null;
  return (
    <>
      <div className="m-h">
        <h1>Accounts</h1>
        <button className="ib" onClick={addAccount} aria-label="Add account">
          <Icon name="plus" />
        </button>
      </div>
      {section(
        'Budget',
        accounts.filter((a) => a.group === 'budget'),
      )}
      {section(
        'Tracking',
        accounts.filter((a) => a.group === 'tracking'),
      )}
      {section(
        'Loans & debts',
        accounts.filter((a) => a.group === 'debt'),
      )}
      {accounts.some((a) => a.group !== 'budget') ? (
        <div className="m-nw" data-testid="accounts-net-worth">
          <span>Net worth</span>
          <b>
            {fmt.num(accounts.reduce((s, a) => s + a.workingBase, 0))} {fmt.base}
          </b>
        </div>
      ) : null}
      <div className="m-list" style={{ marginTop: 14 }}>
        <button className="m-li" onClick={() => navigate('/accounts/all')}>
          <Icon name="search" />
          <div className="t">All transactions</div>
          <Icon name="chevR" size={16} />
        </button>
      </div>
      {!accounts.length ? (
        <div className="empty">
          <h4>No accounts yet</h4>
          <p>Add your bank accounts and cash wallets to start budgeting.</p>
          <button className="btn primary" style={{ marginTop: 16 }} onClick={addAccount}>
            Add account
          </button>
        </div>
      ) : null}
    </>
  );
}
